import { Typography } from '@/components/atoms';
import { MapPinnedIcon } from '@/components/atoms/icons';
import { ImageCarousel } from '@/components/molecules/ImageCarousel';
import { useTranslation, useUIColors } from '@/core/hooks';
import React from 'react';
import { ImageSourcePropType, Pressable, StyleSheet, View } from 'react-native';

export interface ProjectListingHeroCardProps {
    images: ImageSourcePropType[];
    projectName: string;
    location: string;
    availableUnitsCount: number;
    /**
     * Opens the project's location in the native maps app. Omit/undefined
     * when coordinates aren't available from the API — the pin icon is
     * hidden entirely in that case, not just inert. Nested inside the
     * card's own `onPress`; RN's responder system gives the tap to this
     * inner Pressable first, so it doesn't also trigger the card's onPress.
     */
    onMapPress?: () => void;
    /** Opens the mobile-only Project Details screen for this project. */
    onPress?: () => void;
}

/**
 * Project-level hero shown once above the per-unit list on the ADHA home
 * screen's Exclusive Listings section (new per the redesign) — image
 * carousel with an "X Available Units" badge, project name, and location.
 * The unit cards rendered below it use the compact "list" card layout
 * instead of the big-carousel "grid" one, since this hero already carries
 * the carousel.
 */
export function ProjectListingHeroCard({
    images,
    projectName,
    location,
    availableUnitsCount,
    onMapPress,
    onPress,
}: Readonly<ProjectListingHeroCardProps>) {
    const colors = useUIColors();
    const { t } = useTranslation();

    if (!projectName) {
        return null;
    }

    return (
        <Pressable
            onPress={onPress}
            style={[
                styles.container,
                { backgroundColor: colors.backgroundLevel1, borderColor: colors.backgroundLevel2 },
            ]}
        >
            <View style={styles.imageWrap}>
                {/* ImageCarousel's inner ScrollView otherwise swallows taps before
                    they reach the outer Pressable — same pattern PropertyListingCard's
                    grid layout uses so the image area is tappable too, not just the
                    text below it. */}
                <ImageCarousel
                    images={images}
                    height={200}
                    activeDotColor={colors.white}
                    {...(onPress && { onPress })}
                />
                {availableUnitsCount > 0 && (
                    <View style={styles.badge} pointerEvents="none">
                        <Typography variant="footnote" style={styles.badgeText}>
                            {availableUnitsCount} {t('propertiesWeb.list.associatedUnits')}
                        </Typography>
                    </View>
                )}
            </View>

            <View style={styles.content}>
                <View style={styles.titleRow}>
                    <Typography
                        variant="title2"
                        color="primary"
                        numberOfLines={1}
                        ellipsizeMode="tail"
                        style={styles.titleText}
                    >
                        {projectName}
                    </Typography>
                    {onMapPress && (
                        <Pressable
                            onPress={onMapPress}
                            accessibilityRole="button"
                            style={[styles.mapIconWrap, { backgroundColor: colors.backgroundLevel2 }]}
                        >
                            <MapPinnedIcon size={20} color={colors.fontPrimary} />
                        </Pressable>
                    )}
                </View>
                {location ? (
                    <View style={styles.locationRow}>
                        <MapPinnedIcon size={14} color={colors.fontTertiary} />
                        <Typography variant="footnote" color="tertiary">
                            {location}
                        </Typography>
                    </View>
                ) : null}
            </View>
        </Pressable>
    );
}

const styles = StyleSheet.create({
    container: {
        borderRadius: 12,
        padding: 8,
        gap: 8,
        borderWidth: 1,
    },
    imageWrap: {
        height: 200,
        borderRadius: 8,
        overflow: 'hidden',
    },
    // Logical `start` (not `left`): top-left in English, top-right in Arabic.
    badge: {
        position: 'absolute',
        top: 12,
        start: 12,
        backgroundColor: 'rgba(245, 245, 245, 0.9)',
        borderRadius: 8,
        paddingHorizontal: 10,
        paddingVertical: 6,
    },
    badgeText: {
        fontWeight: '600',
    },
    content: {
        gap: 4,
        marginTop: 4,
        paddingHorizontal: 4,
        paddingBottom: 4,
    },
    titleRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
    },
    titleText: {
        flex: 1,
        fontWeight: '700',
    },
    mapIconWrap: {
        width: 36,
        height: 36,
        borderRadius: 18,
        justifyContent: 'center',
        alignItems: 'center',
    },
    locationRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
});

export default ProjectListingHeroCard;
